const SYNC_RGB_LIGHT_EFFECT_SETTINGS_KEY = "sync-rgb:lightEffectSettings";
const SYNC_RGB_SELECTED_IDS_KEY = "sync-rgb:selectedDeviceIds";

function hasSyncRgbPersistedKeyboardSelection(): boolean {
  try {
    const rawIds = window.localStorage.getItem(SYNC_RGB_SELECTED_IDS_KEY);
    if (!rawIds) return false;
    const ids = JSON.parse(rawIds) as unknown;
    return Array.isArray(ids) && ids.length > 0;
  } catch {
    return false;
  }
}

function readSyncRgbPersistedActiveTab(): string | null {
  try {
    const rawSettings = window.localStorage.getItem(
      SYNC_RGB_LIGHT_EFFECT_SETTINGS_KEY
    );
    const settings = rawSettings
      ? (JSON.parse(rawSettings) as { activeTab?: string })
      : null;
    return settings?.activeTab ?? null;
  } catch {
    return null;
  }
}

/** 神光同步律动会话进行中：子应用勿 STOP_CAPTURE / 恢复常规灯效 */
export function shouldKeepSyncRgbCaptureAlive(): boolean {
  if (typeof window === "undefined") return false;
  return (
    readSyncRgbPersistedActiveTab() === "rhythm" &&
    hasSyncRgbPersistedKeyboardSelection()
  );
}

/** 神光同步常规灯效会话进行中：子应用勿恢复进页前快照 */
export function shouldKeepSyncRgbRoutineAlive(): boolean {
  if (typeof window === "undefined") return false;
  return (
    readSyncRgbPersistedActiveTab() === "routine" &&
    hasSyncRgbPersistedKeyboardSelection()
  );
}

/** 神光同步任一有效 Tab 会话进行中（律动 / 常规等） */
export function shouldKeepSyncRgbSessionAlive(): boolean {
  if (typeof window === "undefined") return false;
  const tab = readSyncRgbPersistedActiveTab();
  if (tab !== "rhythm" && tab !== "routine" && tab !== "ai") return false;
  return hasSyncRgbPersistedKeyboardSelection();
}

export const SYNC_RGB_RUNTIME_KEY_COLORS_CHANGED_EVENT =
  "sync-rgb:runtimeKeyColorsChanged";

/** 子应用回设备首页：请求主应用侧重绑 SDK 并恢复神光同步实体下发 */
export const SYNC_RGB_HOME_RESUME_SCHEDULE_EVENT =
  "sync-rgb:homeResumeSchedule";

export const SYNC_RGB_HOME_RESUME_PENDING_KEY = "sync-rgb:homeResumePending";

export function markSyncRgbHomeResumePending(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(SYNC_RGB_HOME_RESUME_PENDING_KEY, "1");
  } catch {
    // ignore
  }
}

export function isSyncRgbHomeResumePending(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return (
      window.sessionStorage.getItem(SYNC_RGB_HOME_RESUME_PENDING_KEY) === "1"
    );
  } catch {
    return false;
  }
}

export function clearSyncRgbHomeResumePending(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(SYNC_RGB_HOME_RESUME_PENDING_KEY);
  } catch {
    // ignore
  }
}

export function requestSyncRgbHomeResumeSchedule(): void {
  if (typeof window === "undefined") return;
  markSyncRgbHomeResumePending();
  try {
    window.dispatchEvent(new CustomEvent(SYNC_RGB_HOME_RESUME_SCHEDULE_EVENT));
  } catch {
    // ignore
  }
}

/** 与 shouldKeepSyncRgbSessionAlive 等价，语义用于子应用退出判断 */
export function shouldKeepSyncRgbAliveOnKeyboardSubAppExit(): boolean {
  return shouldKeepSyncRgbSessionAlive();
}

/** 神光同步页内实时律动 canvas 已完成首帧采样（常驻离屏 runtime 可交接） */
export const SYNC_RGB_PAGE_LIVE_RHYTHM_SAMPLING_READY_EVENT =
  "sync-rgb:pageLiveRhythmSamplingReady";

/** 神光同步页内 AI canvas 已完成首帧采样 */
export const SYNC_RGB_PAGE_AI_SAMPLING_READY_EVENT =
  "sync-rgb:pageAiSamplingReady";

/** 神光同步页内 GIF canvas 已完成首帧采样 */
export const SYNC_RGB_PAGE_GIF_SAMPLING_READY_EVENT =
  "sync-rgb:pageGifSamplingReady";

export function notifySyncRgbPageLiveRhythmSamplingReady(): void {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(
      new CustomEvent(SYNC_RGB_PAGE_LIVE_RHYTHM_SAMPLING_READY_EVENT)
    );
  } catch {
    // ignore
  }
}

export function notifySyncRgbPageAiSamplingReady(): void {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(
      new CustomEvent(SYNC_RGB_PAGE_AI_SAMPLING_READY_EVENT)
    );
  } catch {
    // ignore
  }
}

export function notifySyncRgbPageGifSamplingReady(): void {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(
      new CustomEvent(SYNC_RGB_PAGE_GIF_SAMPLING_READY_EVENT)
    );
  } catch {
    // ignore
  }
}
